import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Devices } from '../../../../models/Accounts';
import { InfoItem, IptvPermission } from '../../../../pages/ServiceManagement';
import { Info } from '../Info';
import { TextButton, TextButtonClassNames } from '../../../commons/buttons/TextButton';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { ReactComponent as EditPencilIcon } from '../../../../assets/images/EditPencil.svg';
import { ButtonClassNames } from '../../../../models/Common';
import { ReactComponent as DeleteIcon } from '../../../../assets/images/Delete.svg';
import { BasicTable } from '../../../commons/customTables/BasicTable';
import { CustomTable, CustomTableClassNames, TableBodyCell } from '../../../../models/CustomTable';
import { amountIptvDevices } from '../../../../constants/configuration';

interface IptvDevicesProps {
  iptvDevices: Devices[];
  iptvAccountId: string | number;
  handleAdd: (id: number) => void;
  handleEdit: (id: number, mac: string, deviceId: number, login: string) => void;
  handleDelete: (id: number, deviceId: number, mac: string) => void;
  iptvPermissions: IptvPermission;
}

export const IptvDevices: FC<IptvDevicesProps> = ({
  iptvDevices,
  iptvAccountId,
  handleAdd,
  handleEdit,
  handleDelete,
  iptvPermissions,
}) => {
  const { t } = useTranslation();

  const getIptvDevices = (devices: Devices[]): TableBodyCell[] => {
    return devices.map((el, index) => {
      return {
        id: el.id,
        bodyCells: [
          el.Login,
          el.MAC,
          <div className="control">
            <CustomMUIButton
              label={<EditPencilIcon />}
              handleClick={() => handleEdit(+iptvAccountId, el.MAC, el.id, el.Login)}
              className={ButtonClassNames.IconFulfilled}
              tooltipText={`${t('tooltip.change')}`}
              isDisabled={index >= amountIptvDevices || !iptvPermissions.canEdit}
            />
            <CustomMUIButton
              label={<DeleteIcon />}
              handleClick={() => handleDelete(+iptvAccountId, el.id, el.MAC)}
              className={ButtonClassNames.IconFulfilled}
              tooltipText={`${t('tooltip.delete')}`}
              isDisabled={!iptvPermissions.canRemove}
            />
          </div>,
        ],
      };
    });
  };

  const iptvDevicesTable: CustomTable = {
    tableHead: [`${t('common.login')}`, `${t('serviceManagement.macAddress')}`],
    tableBodyCells: getIptvDevices(iptvDevices),
  };
  const isShowFootnote = iptvDevices.length >= 5;

  const getAddIptvDevice = (array: Devices[]) => {
    if (array.length < 5) {
      return (
        <TextButton
          label={`${t('buttons.addDevice')} +`}
          handleClick={() => handleAdd(+iptvAccountId)}
          className={TextButtonClassNames.PrimaryActive}
          isDisabled={!iptvPermissions.canAdd}
        />
      );
    }
    return undefined;
  };

  const iptvDevicesSettings: InfoItem[] = [
    { label: t('serviceManagement.account'), value: iptvAccountId },
    {
      label: t('serviceManagement.attachedDevices'),
      value: (
        <BasicTable
          tableHead={iptvDevicesTable.tableHead}
          tableBodyCells={iptvDevicesTable.tableBodyCells}
          caption={getAddIptvDevice(iptvDevices)}
          className={CustomTableClassNames.CursorDisablePointerRow}
        />
      ),
    },
  ];

  return (
    <Info
      infoItems={iptvDevicesSettings}
      title={t('serviceManagement.iptvDevices')}
      moreDevices={t('serviceManagement.moreIptvDevices')}
      isShowFootnote={isShowFootnote}
    />
  );
};
